/**
 * 资源锁管理器 - 基于 p-limit 的按资源串行化
 *
 * 用途：
 * - 保证同一 uploadId 的多次 updatePart 操作串行执行
 * - 不同 uploadId 之间仍然可以并行执行
 * - 自动清理空闲的 limiter，避免内存泄漏
 */

const pLimit = require('p-limit');

class ResourceLockManager {
    /**
     * 每个资源一个独立的限制器（并发度=1）
     * @type {Map<string, ReturnType<typeof pLimit>>}
     */
    static limiters = new Map();

    /**
     * 获取或创建资源的限制器
     * @param {string} resourceId - 资源标识符（如 uploadId）
     * @returns {ReturnType<typeof pLimit>} p-limit 实例
     */
    static getLimiter(resourceId) {
        if (!this.limiters.has(resourceId)) {
            // 每个资源的并发度为 1，即串行执行
            this.limiters.set(resourceId, pLimit(1));
        }
        return this.limiters.get(resourceId);
    }

    /**
     * 在资源锁下执行操作
     * @param {string} resourceId - 资源标识符
     * @param {Function} operation - 要执行的异步操作
     * @returns {Promise<any>} 操作结果
     */
    static async executeExclusive(resourceId, operation) {
        const limiter = this.getLimiter(resourceId);

        try {
            return await limiter(operation);
        } finally {
            // 清理：如果队列为空，删除 limiter（避免内存泄漏）
            // pendingCount: 等待执行的任务数
            // activeCount: 正在执行的任务数
            if (limiter.pendingCount === 0 && limiter.activeCount === 0) {
                this.limiters.delete(resourceId);
            }
        }
    }

    /**
     * 取消资源的待处理操作
     * @param {string} resourceId - 资源标识符
     */
    static cancel(resourceId) {
        if (this.limiters.has(resourceId)) {
            this.limiters.delete(resourceId);
        }
    }

    /**
     * 获取状态（用于调试和监控）
     * @returns {Object} 各资源的队列状态
     */
    static getStatus() {
        const status = {};
        this.limiters.forEach((limiter, resourceId) => {
            status[resourceId] = {
                pending: limiter.pendingCount,
                active: limiter.activeCount,
            };
        });
        return status;
    }

    /**
     * 清理所有锁（用于测试）
     */
    static clearAll() {
        this.limiters.clear();
    }
}

module.exports = ResourceLockManager;
